import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { Phone, MapPin, Clock, Star, Award, Shield, Sparkles, Syringe, Heart, User, Smile, Facebook, Instagram } from 'lucide-react';
import { testimonials } from '../mock';
import BookingModal from '../components/BookingModal';
import GalleryLightbox from '../components/GalleryLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const resolveImg = (url) => (url && url.startsWith('http') ? url : `${BACKEND_URL}${url}`);

const categoryLabel = (category) => {
  const map = {
    all: 'All',
    lips: 'Lips',
    wrinkles: 'Anti Wrinkles',
    fillers: 'Fillers',
    jawline: 'Jawline',
    teartroughs: 'Tear Troughs',
    facerejuvenation: 'Face Rejuvenation',
    chinfiller: 'Chin Filler'
  };
  return map[category] || category.charAt(0).toUpperCase() + category.slice(1);
};

const iconMap = {
  Sparkles: Sparkles,
  Syringe: Syringe,
  Heart: Heart,
  Star: Star,
  User: User,
  Smile: Smile
};

const Home = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [activeGallery, setActiveGallery] = useState('all');
  const [services, setServices] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [priceList, setPriceList] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    axios.get(`${API}/content/services`).then((r) => setServices(r.data)).catch(() => {});
    axios.get(`${API}/content/gallery`).then((r) => setGalleryImages(r.data)).catch(() => {});
    axios.get(`${API}/content/pricing`).then((r) => setPriceList(r.data)).catch(() => {});
  }, []);

  const galleryCategories = ['all', ...Array.from(new Set(galleryImages.map((img) => img.category)))];

  const filteredGallery = activeGallery === 'all' ?
  galleryImages :
  galleryImages.filter((img) => img.category === activeGallery);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b-2 border-[#D4AF37] z-50 shadow-sm">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="https://customer-assets.emergentagent.com/job_c974e444-1b01-42b6-b195-4121c5fc3959/artifacts/7qqfsw1z_Angelina%20White%20Aesthetics%20Logo.jpg"
                alt="Angelina White Aesthetics Logo"
                className="h-16 w-auto object-contain" />

            </div>
            <div className="hidden md:flex space-x-8">
              <a href="#home" className="text-black hover:text-[#D4AF37] transition-colors duration-300 font-medium">Home</a>
              <a href="#services" className="text-black hover:text-[#D4AF37] transition-colors duration-300 font-medium">Services</a>
              <a href="#gallery" className="text-black hover:text-[#D4AF37] transition-colors duration-300 font-medium">Gallery</a>
              <a href="#about" className="text-black hover:text-[#D4AF37] transition-colors duration-300 font-medium">About</a>
              <a href="#pricing" className="text-black hover:text-[#D4AF37] transition-colors duration-300 font-medium">Pricing</a>
              <a href="#testimonials" className="text-black hover:text-[#D4AF37] transition-colors duration-300 font-medium">Testimonials</a>
              <a href="#contact" className="text-black hover:text-[#D4AF37] transition-colors duration-300 font-medium">Contact</a>
            </div>
            <Button
              data-testid="header-pricing-btn"
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#D4AF37] hover:bg-[#B8941F] text-white font-semibold px-6 transition-all duration-300 hover:scale-105">

              Treatment Pricing
            </Button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-6 relative overflow-hidden min-h-screen">
        {/* Background Image */}
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://customer-assets.emergentagent.com/job_c974e444-1b01-42b6-b195-4121c5fc3959/artifacts/yz6dumjh_angelina%20white%20homepage.webp"
            alt="Angelina White Aesthetics"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: 'center center' }}
          />
          <style>{`
            @media (max-width: 768px) {
              #home img {
                object-position: 85% center !important;
              }
            }
          `}</style>
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/90 to-white/85 !opacity-[30%]"></div>
        </div>

        {/* Hero Content */}
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6 px-6 py-2 border-2 border-[#D4AF37] rounded-full bg-white/90 backdrop-blur-sm">
              <p className="text-[#D4AF37] font-semibold">Premium Aesthetic Treatments in Hertfordshire</p>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-black mb-6 leading-tight drop-shadow-lg">
              Transform the New You with Expert Aesthetic Treatments
            </h1>
            <p className="text-xl text-gray-900 mb-8 max-w-2xl mx-auto font-medium drop-shadow-md">
              Experience the highest quality aesthetic treatments using premium brands including Revolax, Azzalure, Plenhyage, Jalupro, Profhilo, Allergan, Juvederm, Teosyal, Botox, and Restylane.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="hero-pricing-btn"
                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                className="bg-[#D4AF37] hover:bg-[#B8941F] text-white font-semibold px-8 py-6 text-lg transition-all duration-300 hover:scale-105 shadow-xl">
                Treatment Pricing
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white font-semibold px-8 py-6 text-lg transition-all duration-300 bg-white/90 backdrop-blur-sm shadow-xl"
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}>
                View Services
              </Button>
            </div>
            
            {/* Trust Indicators */}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
              <div className="flex flex-col items-center p-6 border-2 border-[#D4AF37] rounded-lg hover:shadow-lg transition-shadow duration-300 bg-white/90 backdrop-blur-sm">
                <Award className="w-12 h-12 text-[#D4AF37] mb-3" />
                <h3 className="font-semibold text-black mb-1">Certified Expert</h3>
                <p className="text-sm text-gray-700 text-center">Fully qualified and insured practitioner</p>
              </div>
              <div className="flex flex-col items-center p-6 border-2 border-[#D4AF37] rounded-lg hover:shadow-lg transition-shadow duration-300 bg-white/90 backdrop-blur-sm">
                <Shield className="w-12 h-12 text-[#D4AF37] mb-3" />
                <h3 className="font-semibold text-black mb-1">Premium Brands</h3>
                <p className="text-sm text-gray-700 text-center">Only trusted, high-quality products</p>
              </div>
              <div className="flex flex-col items-center p-6 border-2 border-[#D4AF37] rounded-lg hover:shadow-lg transition-shadow duration-300 bg-white/90 backdrop-blur-sm">
                <Star className="w-12 h-12 text-[#D4AF37] mb-3" />
                <h3 className="font-semibold text-black mb-1">5-Star Rated</h3>
                <p className="text-sm text-gray-700 text-center">Trusted by clients across Hertfordshire</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Our Services</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Expertly delivered treatments using the finest brands in aesthetic medicine
            </p>
          </div>
          
          <Carousel
            opts={{
              align: "start",
              loop: true
            }}
            className="w-full max-w-7xl mx-auto">

            <CarouselContent>
              {services.map((service) => {
                const IconComponent = iconMap[service.icon] || Sparkles;
                return (
                  <CarouselItem key={service.id} className="md:basis-1/2 lg:basis-1/3">
                    <div className="p-2">
                      <Card className="border-2 border-gray-200 hover:border-[#D4AF37] transition-all duration-300 hover:shadow-xl bg-white group h-full">
                        <CardHeader>
                          <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                            <IconComponent className="w-8 h-8 text-white" />
                          </div>
                          <CardTitle className="text-2xl text-black">{service.title}</CardTitle>
                          <CardDescription className="text-gray-600 text-base">{service.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="flex flex-wrap gap-2">
                            {service.brands.map((brand, idx) =>
                            <Badge key={idx} variant="outline" className="border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5">
                                {brand}
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </CarouselItem>);

              })}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-12 border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white" />
            <CarouselNext className="hidden md:flex -right-12 border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white" />
          </Carousel>
          
          <div className="text-center mt-8">
            <p className="text-gray-600 text-sm">← Swipe or click arrows to view more treatments →</p>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 px-6 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Before & After Gallery</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto mb-8">
              See the transformative results of our treatments
            </p>
            
            {/* Gallery Filters */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {galleryCategories.map((category) =>
              <Button
                key={category}
                data-testid={`gallery-filter-${category}`}
                onClick={() => setActiveGallery(category)}
                variant={activeGallery === category ? 'default' : 'outline'}
                className={activeGallery === category ?
                'bg-[#D4AF37] hover:bg-[#B8941F] text-white' :
                'border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white'}>

                  {categoryLabel(category)}
                </Button>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredGallery.map((item, idx) => (
              <div
                key={item.id}
                data-testid={`gallery-item-${item.id}`}
                onClick={() => setLightboxIndex(idx)}
                className="group relative overflow-hidden rounded-lg border-2 border-[#D4AF37] hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="relative">
                  <img
                    src={resolveImg(item.image)}
                    alt={item.title}
                    className="w-full h-auto md:h-64 object-contain md:object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <p className="text-white font-semibold text-lg">{item.title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="md:text-5xl !font-bold !text-4xl mb-4 text-black">About Angelina White Aesthetics</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          </div>
          
          <div className="bg-white p-8 md:p-12 rounded-lg border-2 border-[#D4AF37] shadow-lg">
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Welcome to Angelina White Aesthetics, your premier destination for expert aesthetic treatments in Hertfordshire. At Angelina White Aesthetics, we offer the most innovative non-surgical aesthetic treatments, which include dermal fillers and cosmetic anti-wrinkle procedures.
            </p>
            <p className="!text-lg mb-6 text-gray-700">
              We use only the finest products from trusted brands including <span className="font-semibold text-[#D4AF37]">Revolax, Azzalure, Plenhyage, Jalupro, Profhilo, Allergan, Juvederm, Teosyal, Botox, and Restylane</span> to ensure you receive safe, effective, and natural-looking results.
            </p>
            <p className="!text-lg mb-6 text-gray-700">
              Every treatment begins with a thorough consultation to understand your goals and create a customised treatment plan. A <span className="font-semibold text-[#D4AF37]">free 15 min consultation</span> is provided.
            </p>
            <p className="!text-lg text-gray-700">Your comfort, safety, and satisfaction are our top priorities.

            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-6 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Client Testimonials</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Hear what our satisfied clients have to say
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial) =>
            <Card key={testimonial.id} className="border-2 border-[#D4AF37] bg-white hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) =>
                  <Star key={i} className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
                  )}
                  </div>
                  <p className="text-gray-700 text-lg mb-6 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-black text-lg">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.treatment}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Treatment Pricing</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-700">Transparent pricing for all treatments</p>
          </div>
          
          <div className="bg-white border-2 border-[#D4AF37] rounded-lg overflow-hidden shadow-lg">
            <Accordion type="single" collapsible className="w-full">
              {priceList.map((item, index) =>
              <AccordionItem key={index} value={`item-${index}`} className="border-b border-gray-200 last:border-0">
                  <AccordionTrigger className="px-8 py-6 hover:bg-[#D4AF37]/5 text-left">
                    <div className="flex justify-between w-full pr-4">
                      <span className="font-semibold text-black text-lg">{item.treatment}</span>
                      <span className="text-[#D4AF37] font-bold text-lg">{item.price}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-8 pb-6 text-gray-600">
                    {item.treatment === "Male Anti-Wrinkles - Additional Product" ? (
                      <>
                        <p className="mb-2">Some clients may need additional product depending on muscle strength or treatment area. Male clients often require this due to naturally stronger muscles, but the additional charge only applies if any extra product is used.</p>
                        <p>Price includes consultation and aftercare advice.</p>
                      </>
                    ) : (
                      <p>Price includes consultation and aftercare advice.</p>
                    )}
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Get In Touch</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-700">Book your consultation today</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-2 border-[#D4AF37] bg-white">
              <CardHeader>
                <CardTitle className="text-2xl text-black">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-black mb-1">Location</p>
                    <p className="text-gray-700">Borehamwood, WD6, Hertfordshire</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-black mb-1">Opening Hours</p>
                    <p className="text-gray-700">Monday - Friday: 10am - 6pm</p>
                    <p className="text-gray-700">Saturday: 10am - 4pm</p>
                    <p className="text-gray-700">Sunday: Closed</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <Facebook className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-black mb-1">Facebook</p>
                    <a href="https://facebook.com/angelinawhiteaesthetics" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-[#D4AF37] transition-colors">
                      angelinawhiteaesthetics
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-black mb-1">Instagram</p>
                    <a href="https://instagram.com/angelinawhiteaesthetics" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-[#D4AF37] transition-colors">
                      @angelinawhiteaesthetics
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-[#D4AF37] bg-white">
              <CardHeader>
                <CardTitle className="text-2xl text-black">Ready to Book?</CardTitle>
                <CardDescription className="text-gray-600 text-base">
                  Click below to schedule your consultation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <a
                  href="https://wa.me/447765920294"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block">

                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white font-semibold py-6 text-lg transition-all duration-300 flex items-center justify-center gap-2">

                    <svg
                      className="w-6 h-6"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg">

                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                    Prefer to use WhatsApp
                  </Button>
                </a>
                
                <p className="text-sm text-gray-600 mt-4 text-center">
                  Free consultation • Expert advice • Premium products
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section id="reviews" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Client Reviews</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              See what our clients say about us on Google
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-lg border-2 border-[#D4AF37] shadow-lg">
            <div className="flex flex-col items-center justify-center space-y-6">
              <div className="flex items-center space-x-4">
                <img
                  src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
                  alt="Google"
                  className="h-8" />

                <div className="flex items-center space-x-2">
                  <div className="flex">
                    {[...Array(5)].map((_, i) =>
                    <Star key={i} className="w-6 h-6 fill-[#D4AF37] text-[#D4AF37]" />
                    )}
                  </div>
                  <span className="text-gray-700 font-semibold text-lg">5.0 Rating</span>
                </div>
              </div>
              
              <p className="text-gray-700 text-center text-lg max-w-2xl">
                Visit our Google Business page to read authentic reviews from our satisfied clients and see why Angelina White Aesthetics is Borehamwood's trusted choice for aesthetic treatments.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mt-4">
                <a
                  href="https://www.google.com/search?q=angelina+white+aesthetics"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block">

                  <Button
                    size="lg"
                    className="bg-[#D4AF37] hover:bg-[#B8941F] text-white font-semibold px-8 py-6 text-lg transition-all duration-300 hover:scale-105">

                    View Google Reviews
                  </Button>
                </a>
                <a
                  href="https://www.google.com/search?q=angelina+white+aesthetics&action=write"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block">

                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white font-semibold px-8 py-6 text-lg transition-all duration-300">

                    Leave a Review
                  </Button>
                </a>
              </div>
              
              <p className="text-sm text-gray-600 text-center mt-6">
                Your feedback helps us continue providing exceptional aesthetic treatments
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-6 border-t-4 border-[#D4AF37]">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-white p-3 rounded-lg">
                  <img
                    src="https://customer-assets.emergentagent.com/job_c974e444-1b01-42b6-b195-4121c5fc3959/artifacts/7qqfsw1z_Angelina%20White%20Aesthetics%20Logo.jpg"
                    alt="Angelina White Aesthetics Logo"
                    className="h-12 w-auto object-contain" />

                </div>
              </div>
              <p className="text-gray-400">
                Premium aesthetic treatments in Hertfordshire using the finest brands.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4 text-[#D4AF37]">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#home" className="text-gray-400 hover:text-[#D4AF37] transition-colors">Home</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-[#D4AF37] transition-colors">Services</a></li>
                <li><a href="#gallery" className="text-gray-400 hover:text-[#D4AF37] transition-colors">Gallery</a></li>
                <li><a href="#about" className="text-gray-400 hover:text-[#D4AF37] transition-colors">About</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-[#D4AF37] transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4 text-[#D4AF37]">Premium Brands</h4>
              <div className="flex flex-wrap gap-2">
                {['Revolax', 'Azzalure', 'Plenhyage', 'Jalupro', 'Profhilo', 'Allergan', 'Juvederm', 'Teosyal', 'Botox', 'Restylane'].map((brand) =>
                <Badge key={brand} variant="outline" className="border-[#D4AF37] text-[#D4AF37]">
                    {brand}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">
              © {new Date().getFullYear()} Angelina White Aesthetics. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        data-testid="floating-whatsapp-btn"
        href="https://wa.me/447765920294"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Message us on WhatsApp"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold pl-4 pr-5 py-3 rounded-full shadow-2xl transition-all duration-300 hover:scale-105 animate-bounce-slow">
        <svg
          className="w-7 h-7 flex-shrink-0"
          fill="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
        <span className="hidden sm:inline text-sm">Message us on WhatsApp</span>
      </a>

      {/* Gallery Lightbox */}
      <GalleryLightbox
        images={filteredGallery}
        index={lightboxIndex}
        onNavigate={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        resolveImg={resolveImg}
      />

      {/* Booking Modal */}
      <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
    </div>);

};

export default Home;